// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-11-20 01:19:29 +0100 (Thu, 20 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158411 $
//  Purpose   : tree parts
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
import {JSONTree} from 'uix/chunks'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {funcPart} from 'uix/parts'
import log from 'uix/logging'
import {has, dict, mapItems, get, first, isNumber, isDigit} from 'ldash'
import {AutoPortal} from 'uix/components'
import {useSelector, useSelectorShallow} from 'uix/hooks'

// ---Local Imports
import {isSlimLinkId} from './utils'
import {NTYPE} from './rules'
import {MinorTabGroup} from './tabs'
import {Tree} from './components'

// ---Exports
export {TreeTopTabs, MajorTabGroup} from './tabs'
export {TreeList} from './list'

export const TreeInfo = ({}) => {
  let tree = useSelector(root => root.data.tree)
  return <JSONTree hideRoot={true} data={tree} />
}

const partStyle = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  // width: '25vw',
}

// load tree chunks early
export const AdminTree = funcPart(
  {partStyle, getChunkedUtils: ['TreeUtils'], getGraphics: ['types', 'common', 'tree', 'states']},
  () => {
    /* tree for backend
     */
    let {treechosenTabs, allClassCfgs, request, treePathStyle} = useSelectorShallow(root => ({
      // for tab understanding
      treechosenTabs: root.data.tree.chosenTabs,
      allClassCfgs: Sel.allDomainClassCfgs(root),
      request: Sel.request(root),
      // either from external props
      // or from persisted value (raw read access of ui.prop from FilterNodesItem)
      treePathStyle:
        root.ui.byId['Global/navBarTarget']?.value == 'topPortal' ? false : 'breadcrumb',
    }))

    const onFocusChange = ({node, VTree, prevNode, props}) => {
      if (prevNode && prevNode.id == node.id) {
        // no pseudo routing if we refocus a focused node
        return
      }
      let {domains, treeById} = props

      // ignore treechosenTabs if switch between NTYPE.VLTYPE and datatype does not change
      let datatypeChanged = false

      if (!prevNode) {
        log.warn('no prevNode')
      } else if (prevNode.nodeType == NTYPE.VLTYPE && node.nodeType == NTYPE.VLTYPE) {
        datatypeChanged =
          treeById[prevNode.slimlink || prevNode.otid].datatype !==
          treeById[node.slimlink || node.otid].datatype
      }

      try {
        let applyConfigRules = (newRequest, tabNames, targetDomainClassCfg) => {
          let chosenView = treechosenTabs[node.nodeType]
          let [chosenClassname, chosenTabname] = chosenView || ['', '']
          let available = (cn, tn) =>
            tabNames.some(([classname, tabname]) => cn == classname && (!tn || tn == tabname))

          if (tabNames.length == 0) {
            log.warn('no tabs available')
          }
          let keep = !datatypeChanged && available(newRequest.classname, newRequest.tabname)
          if (!keep && available(chosenClassname, chosenTabname)) {
            // reuse last user choice
            newRequest = {...newRequest, classname: chosenClassname, tabname: chosenTabname}
          }

          if (!available(newRequest.classname, newRequest.tabname)) {
            // find a new view (classname/tabname)
            let _get = (cn, tn) => targetDomainClassCfg[cn]?.tabs[tn]
            let tabcfg = _get(newRequest.classname, newRequest.tabname)
            let preferedMinorTab = null
            if (tabcfg?.minorTabGroup) {
              let tabNamesMinorGroup = tabNames.filter(
                ([cn, tn]) => _get(cn, tn)?.minorTabGroup === tabcfg.minorTabGroup
              )
              if (tabNamesMinorGroup.length > 0) {
                preferedMinorTab = tabNamesMinorGroup[0]
              }
            }

            if (available(newRequest.classname)) {
              // at least current classname is still available
              if (preferedMinorTab) {
                newRequest = {
                  ...newRequest,
                  classname: preferedMinorTab[0],
                  tabname: preferedMinorTab[1],
                }
              } else if (newRequest.id) {
                // select first tabname for classname
                let remaining = tabNames.filter(([classname]) => newRequest.classname == classname)
                let [classname, tabname] = remaining[0]
                newRequest = {...newRequest, classname, tabname}
              } else {
                // fallback to classname only
                newRequest = {...newRequest, classname: newRequest.classname, tabname: null}
              }
            } else {
              // fallback to first available classname/tabname
              let [classname, tabname] = tabNames[0]
              newRequest = {...newRequest, classname, tabname}
            }
          }
          return newRequest
        }

        // XXX - HACK: the target domain can be unloaded
        // so we hack the request classCfg into all unitialized ones
        // this is not correct (but mostly ok)
        // XXX: we need to initialize the domain earlier
        // XXX - 2020-01-28: pretty sure the above has been done
        let filledAllClassCfgs = dict(
          mapItems(allClassCfgs, (k, v) => [k, v || allClassCfgs[request.dname]])
        )
        let rule = VTree._getRule(node, {...props, allClassCfgs: filledAllClassCfgs})
        let tabNames = get(rule, 'tabs', () => [])(node)

        if (tabNames.length == 0) {
          log.warn('no tabs configured for %o - fallback to tree/id', node.nodeType)
          tabNames = [['tree', 'id']]
        }
        // we need to understand otids of links here
        // try to preserve class/tab
        let newRequest = {classname: request.classname, tabname: request.tabname}
        if (request.ctxid && request.ctxid === node.id) {
          newRequest.id = 0
        } else if (node.nodeType == 'domain') {
          // preserve non-numericals like mods/forms/borndigital?
          if (
            request.id &&
            !isDigit(request.id) &&
            !String(request.id).includes('~') &&
            !isSlimLinkId(request.id)
          ) {
            newRequest.id = request.id
          } else {
            newRequest.id = null
          }
        } else if (node.slimlink) {
          newRequest.id = node.slimlink
        } else if (node.otid) {
          newRequest.id = node.otid
        } else {
          newRequest.id = node.id
        }
        if (isNumber(node.odid)) {
          newRequest.dname = domains.odid2name[node.odid]
          //~ newRequest.odid = node.odid
        }

        let requestWithAppliedRules = applyConfigRules(
          newRequest,
          tabNames,
          filledAllClassCfgs[newRequest.dname]
        )
        return requestWithAppliedRules
      } catch (e) {
        log.exception(e)
      }
    }
    return (
      <Tree
        showTopMenu
        showBottomMenu
        syncWithRequest
        onFocusChange={onFocusChange}
        treePathStyle={treePathStyle}
      />
    )
  }
)

export const TestTreeSync = funcPart({partStyle}, () => <Tree syncWithRequest />)
export const TestTree = funcPart({partStyle}, () => <Tree showBottomMenu />)

export const MinorTabGroupBottom = funcPart({wrapped: false}, () => {
  return (
    <AutoPortal target="centerBottomPortal">
      <MinorTabGroup bottom />
    </AutoPortal>
  )
})
